import React, { useRef, useMemo, useState} from 'react';
import "./TextEditor.scss";
import JoditEditor from 'jodit-react';
import {
    alignTextCenterIcon as AlignTextCenterSvg,
    alignTextJustifyIcon as AlignTextJustifySvg,
    alignTextLeftIcon as AlignTextLeftSvg,
    alignTextRightIcon as AlignTextRightSvg,
    formatBoldIcon as FormatBoldSvg,
    formatColorIcon as FormatColorSvg,
    formatItalicIcon as FormatItalicSvg,
    formatListNumberedIcon as FormatListNumberedSvg,
    formatListBulletedIcon as FormatListBulletedSvg,
    formatUnderlineIcon as FormatUnderlineSvg,
    attachmentIcon as AttachmentSvg
} from "../Icon/Icon";
import {renderToStaticMarkup} from "react-dom/server";
import classNames from "classnames";
import Select from "../Input/Select";
import {getTextSizeFromHtml, wrap, unwrap, textSizeOptions} from "./textEditorHelpers";

const TextEditor = ({value, startSize, onTextChange, onSizeChange, error, onBlur, showTextSize = false}) => {
    const editor = useRef(null);
    const [textSize, setTextSize] = useState(() => getTextSizeFromHtml(startSize));

    const config = useMemo(
        () => ({
            readonly: false,
            placeholder: 'Start typing...',
            toolbarAdaptive: false,
            statusbar: false,
            disablePlugins: "add-new-line",
            colorPickerDefaultTab: 'color',
            editorClassName: showTextSize ? `text-size-${textSize}` : '',
            askBeforePasteHTML: false,
            askBeforePasteFromWord: false,
            defaultActionOnPaste: 'insert_as_html',
            link: {
                noFollowCheckbox: false,
                modeClassName: ''
            },
            buttons: ['left', 'right', 'justify', 'center', 'brush', 'bold', 'italic', 'underline', 'ol', 'ul', 'link'],
            controls: {
                left: {
                    icon: renderToStaticMarkup(<AlignTextLeftSvg/>)
                },
                right: {
                    icon: renderToStaticMarkup(<AlignTextRightSvg/>)
                },
                justify: {
                    icon: renderToStaticMarkup(<AlignTextJustifySvg/>)
                },
                center: {
                    icon: renderToStaticMarkup(<AlignTextCenterSvg/>)
                },
                brush: {
                    icon: renderToStaticMarkup(<FormatColorSvg/>),
                },
                bold: {
                    icon: renderToStaticMarkup(<FormatBoldSvg/>)
                },
                italic: {
                    icon: renderToStaticMarkup(<FormatItalicSvg/>)
                },
                underline: {
                    icon: renderToStaticMarkup(<FormatUnderlineSvg/>)
                },
                ol: {
                    icon: renderToStaticMarkup(<FormatListNumberedSvg/>),
                    list: null
                },
                ul: {
                    icon: renderToStaticMarkup(<FormatListBulletedSvg/>),
                    list: null
                },
                link: {
                    icon: renderToStaticMarkup(<AttachmentSvg/>),
                }
            }
        }),
        [textSize]
    );

    const handleEditorChange = (newContent) => {
        onTextChange(showTextSize ? wrap(newContent, textSize) : newContent)
    }

    const handleSizeChange = (selectedOption) => {
        setTextSize(selectedOption.value);
        onTextChange(wrap(unwrap(value), selectedOption.value));
        if (onSizeChange) {
            onSizeChange(selectedOption.value);
        }
    }

    return (
        <div className={classNames("text-editor", {"wrong-input": error})}>
            {
                showTextSize && (
                    <Select
                        label="Text size"
                        name="textSize"
                        handleChange={handleSizeChange}
                        options={textSizeOptions}
                        value={textSizeOptions.find(option => option.value === textSize)}
                        inputWrapperClassName="size-select"
                    />
                )
            }

            <JoditEditor
                ref={editor}
                value={unwrap(value)}
                config={config}
                onChange={handleEditorChange}
                onBlur={onBlur}
            />
            {error && <p className="error">{error}</p>}
        </div>

    );
}

export default TextEditor;